import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authApi from './api/auth';
import Header from './components/Header';
import { useAppDispatch, useAppSelector } from './hooks/useRedux';
import Account from './pages/Account';
import Education from './pages/Account/Profile/Education';
import Experience from './pages/Account/Profile/Experience';
import Personal from './pages/Account/Profile/Personal';
import Profession from './pages/Account/Profile/Profession';
import Settings from './pages/Account/Settings';
import Dashboard from './pages/Dashboard/Dashboard';
import Login from './pages/Login/Login';
import Profile from './pages/Profile';
import ProfileSetup from './pages/ProfileSetup/ProfileSetup';
import { login } from './reducers/authSlice';
import { getTutorRequest } from './reducers/profileSetupSlice';
import { getTutorProfile } from './reducers/profileSlice';
import { MyCalender } from './pages/MyCalender';
import { setPreferences } from './reducers/preferences';
import SplashScreen from './components/SplashScreen';
import ComingSoon from './pages/ComingSoon';
import MyPlans from './pages/MyPlans';
import MyStudents from './pages/MyStudents';
import StudentPlans from './pages/MyStudents/StudentPlans';
import Attendance from './pages/Attendance';
import PlanAttendance from './pages/Attendance/PlanAttendance';
import { getCurrencyFromCode, getVisitorCurrencyRate, getVisitorInfo } from './utils/getVisitorInfo';
// @ts-ignore
import CountryQuery from 'country-query';
import { setVisitorInfo } from './reducers/visitorInfoSlice';
import MyEarnings from './pages/MyEarnings';
import Inbox from './pages/Inbox';
import Contact from './pages/Contact';
import xhr from './utils/fetch';
import Legal from './pages/Legal';
import About from './pages/About';
import Notifications from './pages/Notifications';
import Footer from './components/Footer';

function App() {
  const dispatch = useAppDispatch();
  const { tutor } = useAppSelector((state) => state.authSlice);
  const { profile } = useAppSelector((state) => state.profileSlice);
  const [loading, setLoading] = useState(false);
  const { countryCode } = useAppSelector((state) => state.visitorInfoSlice);

  useEffect(() => {
    setLoading(true);
    authApi
      .authStatus()
      .then(async (data) => {
        if (data.status === 'OK') {
          dispatch(login(data.data));
          if (data.data._id) {
            dispatch(getTutorRequest(data.data._id));
            dispatch(getTutorProfile(data.data._id));
          }
          setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    getVisitorInfo().then(async (data) => {
      if (data) {
        const { countryCode } = data;
        if (countryCode) {
          let countryData = CountryQuery.find('cca2', countryCode[1]);
          let currCode = Object.keys(countryData.currencies)[0];
          let currency = getCurrencyFromCode(currCode);
          let dialCode = countryData.idd.root + countryData.idd.suffixes[0];
          let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          let countryName = countryData.name.common;
          getVisitorCurrencyRate(currency.code).then((rate) => {
            dispatch(
              setVisitorInfo({
                countryCode: countryCode[1],
                countryName,
                currency: {
                  ...currency,
                  rate
                },
                dialCode,
                timezone
              })
            );
            dispatch(
              setPreferences({
                timezone: timezone,
                currency: {
                  ...currency,
                  rate
                }
              })
            );
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (tutor?._id) {
      dispatch(getTutorProfile(tutor._id)).then(async (res) => {
        if (res.payload) {
          await xhr({
            url: `/add-user-location`,
            method: 'POST',
            body: {
              userId: res.payload._id,
              role: 'tutor',
              country: countryCode.toLowerCase()
            }
          })
          return;
        } else {
          dispatch(getTutorRequest(tutor._id));
        }
      });
    }
  }, [tutor]);

  useEffect(() => {
    if (profile) {
      dispatch(setPreferences({ timezone: profile.prefrences?.timezone, currency: profile.prefrences?.currency }));
    }
  }, [profile]);

  if (loading) {
    return <SplashScreen />;
  }

  return (
    <div className="px] mx-auto">
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/contact" element={<Contact />} />
          <Route path='/legal/:type' element={<Legal />} />
          <Route path='/about' element={<About />} />

          <Route path="/" element={<Layout />}>
            <Route path="/profilesetup" element={<ProfileSetup />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/calendar" element={<MyCalender />} />
            <Route path="/attendance" element={<Attendance />} />
            <Route path="/plans/:planId/attendance" element={<PlanAttendance />} />
            <Route path="/plans" element={<MyPlans />} />
            <Route path="/students" element={<MyStudents />} />
            <Route path="/students/:id" element={<StudentPlans />} />
            <Route path="/earnings" element={<MyEarnings />} />
            <Route path="/inbox" element={<Inbox />} />

            <Route path="/profile" element={<Profile />} />
            <Route path="/account" element={<Account />}>
              <Route index element={<Personal />} />
              <Route path="personal" element={<Personal />} />
              <Route path="education" element={<Education />} />
              <Route path="experience" element={<Experience />} />
              <Route path="subjects" element={<Profession />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="/notifications" element={<Notifications />} />
          </Route>
          <Route path="/profilesetup" element={<ProfileSetup />} />
          <Route path="/*" element={<ComingSoon />} />
        </Routes>
      </Router>
      <ToastContainer position="bottom-right" theme="light" />
    </div>
  );
}

const Layout = () => {
  const { tutor } = useAppSelector((state) => state.authSlice);
  const [sidebarOpen, setSideBarOpen] = useState(false);

  const onOpenSidebar = () => {
    setSideBarOpen(true);
    document.body.style.height = '100%';
    document.body.style.overflow = 'hidden';
  };
  const onCloseSidebar = () => {
    setSideBarOpen(false);
    document.body.style.height = '100%';
    document.body.style.overflow = 'auto';
  };

  if (!tutor?._id) {
    return <Navigate to={'/login'} />;
  }

  return (
    <div className="w-full h-full overflow-hidden ">
      <Header sidebarOpen={sidebarOpen} onOpenSidebar={onOpenSidebar} onCloseSidebar={onCloseSidebar} />
      <div
        onClick={() => onCloseSidebar()}
        className={`container ${sidebarOpen
          ? 'absolute top-[10%] transform translate-x-[70%] transition-all rounded-2xl max-h-[80%] border  overflow-hidden drop-shadow-[-15px_15px_40px_rgba(0,0,0,0.1)] cursor-pointer'
          : 'transition-all pt-16'
          }`}
      >
        <Outlet />
      </div>
      <Footer/>
    </div>
  );
};

export default App;
